import React, { useEffect, useState } from 'react';
import { Row, Col, Spin, Button, Modal, Form, Card, Space, Typography, Tag, InputNumber, Select, Switch, theme } from 'antd';
import { message } from '../../services/toastMessage';
import { PlusOutlined, EditOutlined, StopOutlined, CheckCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import { IconTool } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import AppLayout from '../../components/Layouts/Layout';
import EmptyState from '../../components/EmptyState';
import PageHeaderRow from '../../components/Layouts/PageHeaderRow';
import ConfirmBottomSheet from '../../components/ConfirmBottomSheet';
import FloatingLabelInput from '../../components/FloatingLabelInput';
import EmojiTextArea from '../../components/EmojiTextArea';
import TaxonomyPicker from '../../components/TaxonomyPicker';
import AccessoryTypePicker from '../../components/AccessoryTypePicker';
import PhotoUpload from '../../components/PhotoUpload';
import {
  getMyMachineryAccessories,
  createMachineryAccessory,
  updateMachineryAccessory,
  deleteMachineryAccessory,
} from '../../services/MachineryAccessoryService';
import { IMachineryAccessory, IMachineryAccessorySpecs } from '../../interfaces/heavyDutyMachinery/IMachineryAccessory';
import { useTaxonomy } from '../../hooks/useTaxonomy';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import { useAppBreakpoint } from '../../hooks/useAppBreakpoint';
import { pillToneStyle } from '../../theme/creamyTokens';
import { buildCouplerTypeGroupedOptions, HYDRAULIC_LINES_OPTIONS, HYDRAULIC_CONNECTOR_TYPE_OPTIONS } from '../../constants/machineryAccessorySpecOptions';
import { HeavyDutyMachineryOption } from '../../constants/heavyDutyMachinerySpaceOptions';
import AccessoryDraftAssistant, { AccessoryDraftApplied } from '../../components/machinery/AccessoryDraftAssistant';
import AiFilledMark from '../../components/machinery/AiFilledMark';
import { applyAccessoryDraft, ACCESSORY_CHECK_FIELDS } from '../../helpers/accessoryDraftHelper';

const { Text, Title } = Typography;

/**
 * What openCreateModal puts in the form before anybody has typed anything.
 *
 * Only requiresHydraulics matters to the assistant, and it matters a lot: a
 * switch nobody has touched is not an answer, and reading this `false` as one
 * would keep a breaker's entire hydraulic section empty and hidden. See
 * applyAccessoryDraft.
 */
const ACCESSORY_MODAL_DEFAULTS = { quantity: 1, isIndependentlyRentable: false, requiresHydraulics: false };

interface AccessoryFormValues extends IMachineryAccessorySpecs {
  name: string;
  description?: string;
  pricePerDay: number;
  quantity: number;
  isIndependentlyRentable: boolean;
  compatibleCategories: string[];
  photoUrlsText?: string[];
  photoThumbnailsText?: string[];
}

// FieldGroup-style section header -- this modal's fields are otherwise a
// flat Form.Item list, so a small strong label is enough to read as its own
// block without pulling in the wizard's own heavier FieldGroup component.
const SpecSectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text strong style={{ display: 'block', fontSize: 14, marginTop: 8, marginBottom: 4 }}>
    {children}
  </Text>
);

/**
 * A host's own catalog of accessories/attachments (buckets, hydraulic
 * hammers, ...) -- Fase 11's equivalent of Operator's staff roster (Fase
 * 4ter), same "catalog at the host level, reusable across machines"
 * pattern, but no Operator page exists on the frontend to mirror (only its
 * backend CRUD and the checkout's own operator-selection step were ever
 * built there) -- this instead follows MyHeavyDutyMachineries' own
 * list-plus-create pattern and HOST_TOOLS_NAV's own host-only nav placement
 * (a peer to "Mi flota", not one of MobileBottomNav's 5 fixed tabs).
 * Linking an accessory to a specific machine happens on that machine's own
 * edit form (see HeavyDutyMachineryEditForm), not here.
 */
const MyMachineryAccessoriesContent: React.FC = () => {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const { resolveName: categoryLabel } = useTaxonomy();
  const [accessories, setAccessories] = useState<IMachineryAccessory[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAccessory, setEditingAccessory] = useState<IMachineryAccessory | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [deletingAccessory, setDeletingAccessory] = useState<IMachineryAccessory | undefined>(undefined);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [form] = Form.useForm<AccessoryFormValues>();

  /**
   * Which fields the assistant filled in, so each one can say so -- see
   * AiFilledMark. Names rather than a single flag, because the question being
   * asked at each input is "is THIS number one I have to check", not "was the
   * assistant used".
   */
  const [aiFilledFields, setAiFilledFields] = useState<Set<string>>(new Set());
  const photoUrls = Form.useWatch('photoUrlsText', form);
  const photoThumbnails = Form.useWatch('photoThumbnailsText', form);
  const requiresHydraulics = Form.useWatch('requiresHydraulics', form);

  const opts = (options: HeavyDutyMachineryOption[]) => options.map((o) => ({ label: translate(t, o.labelKey), value: o.value }));

  // Groups each family (Lehnhoff, CW/Verachtert, Nordic...) with its own
  // curated models underneath -- the family's own plain value stays
  // selectable as a "generic" row inside its own group, so picking a
  // specific model is always optional. Families with no known models yet
  // (UniversalSkidSteer/PinOn/Other) render as a plain flat option. See
  // buildCouplerTypeGroupedOptions's own doc comment -- shared with
  // AccessoryFiltersModal's own CouplerType filter.
  const couplerTypeOptions = buildCouplerTypeGroupedOptions(t);

  const load = () => {
    setLoading(true);
    getMyMachineryAccessories()
      .then(setAccessories)
      .catch(() => message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_LOAD_ERROR)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** The assistant's mark for one field, or nothing -- keeps each label to one expression. */
  const aiMark = (field: string) => (
    <AiFilledMark filled={aiFilledFields.has(field)} tone={ACCESSORY_CHECK_FIELDS.includes(field) ? 'check' : 'normal'} />
  );

  /**
   * Everything the assistant worked out, dropped into the modal.
   *
   * Same rule as its machinery sibling, and it lives in applyAccessoryDraft
   * for the same reason: a suggestion never replaces an answer already given,
   * which is what makes the button safe to press twice.
   */
  const handleAiApplied = (applied: AccessoryDraftApplied) => {
    const current = form.getFieldsValue(true) as Record<string, unknown>;
    const { values, filledFields } = applyAccessoryDraft(applied.draft, current, ACCESSORY_MODAL_DEFAULTS);

    const filled = [...filledFields];
    if (applied.accessoryTypeCode && !current.accessoryTypeCode) {
      values.accessoryTypeCode = applied.accessoryTypeCode;
      filled.push('accessoryTypeCode');
    }

    form.setFieldsValue(values as Partial<AccessoryFormValues>);

    // Added to rather than replaced, and without spreading the Set: this
    // project targets ES5, where that needs downlevelIteration.
    setAiFilledFields((previous) => {
      const next = new Set(previous);
      filled.forEach((field) => next.add(field));
      return next;
    });

    // Said out loud, because an assistant that appears to have done nothing is
    // indistinguishable from one that is broken.
    if (filled.length === 0) message.info(translate(t, TRANSLATIONS.MACHINERY_AI_NOTHING_FILLED));
  };

  const openCreateModal = () => {
    setEditingAccessory(undefined);
    form.resetFields();
    form.setFieldsValue(ACCESSORY_MODAL_DEFAULTS);
    setAiFilledFields(new Set());
    setModalOpen(true);
  };

  const openEditModal = (accessory: IMachineryAccessory) => {
    setEditingAccessory(accessory);
    setAiFilledFields(new Set());
    form.setFieldsValue({
      name: accessory.name,
      description: accessory.description,
      pricePerDay: accessory.pricePerDay,
      quantity: accessory.quantity,
      isIndependentlyRentable: accessory.isIndependentlyRentable,
      compatibleCategories: accessory.compatibleCategories.split(',').filter(Boolean),
      weightRangeMinTons: accessory.weightRangeMinTons,
      weightRangeMaxTons: accessory.weightRangeMaxTons,
      couplerType: accessory.couplerType,
      requiresHydraulics: accessory.requiresHydraulics,
      hydraulicFlowMinLMin: accessory.hydraulicFlowMinLMin,
      hydraulicFlowMaxLMin: accessory.hydraulicFlowMaxLMin,
      hydraulicPressureMinBar: accessory.hydraulicPressureMinBar,
      hydraulicPressureMaxBar: accessory.hydraulicPressureMaxBar,
      hydraulicLines: accessory.hydraulicLines,
      requiresHighFlow: accessory.requiresHighFlow,
      requiresCaseDrain: accessory.requiresCaseDrain,
      hydraulicConnectorType: accessory.hydraulicConnectorType,
      workingWidthMm: accessory.workingWidthMm,
      capacityLiters: accessory.capacityLiters,
      ownWeightKg: accessory.ownWeightKg,
      accessoryTypeCode: accessory.accessoryTypeCode,
      photoUrlsText: accessory.photoUrls,
      photoThumbnailsText: accessory.photoUrls,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    const payload = {
      name: values.name.trim(),
      description: values.description?.trim() || undefined,
      pricePerDay: values.pricePerDay,
      quantity: values.quantity,
      isIndependentlyRentable: !!values.isIndependentlyRentable,
      compatibleCategories: values.compatibleCategories.join(','),
      weightRangeMinTons: values.weightRangeMinTons,
      weightRangeMaxTons: values.weightRangeMaxTons,
      couplerType: values.couplerType,
      requiresHydraulics: !!values.requiresHydraulics,
      hydraulicFlowMinLMin: values.hydraulicFlowMinLMin,
      hydraulicFlowMaxLMin: values.hydraulicFlowMaxLMin,
      hydraulicPressureMinBar: values.hydraulicPressureMinBar,
      hydraulicPressureMaxBar: values.hydraulicPressureMaxBar,
      hydraulicLines: values.hydraulicLines,
      requiresHighFlow: values.requiresHighFlow,
      requiresCaseDrain: values.requiresCaseDrain,
      hydraulicConnectorType: values.hydraulicConnectorType,
      workingWidthMm: values.workingWidthMm,
      capacityLiters: values.capacityLiters,
      ownWeightKg: values.ownWeightKg,
      accessoryTypeCode: values.accessoryTypeCode,
      photoUrls: values.photoUrlsText ?? [],
    };

    setSaving(true);
    try {
      const saved = editingAccessory
        ? await updateMachineryAccessory(editingAccessory.id, payload)
        : await createMachineryAccessory(payload);
      setAccessories((prev) =>
        editingAccessory ? prev.map((a) => (a.id === saved.id ? saved : a)) : [saved, ...prev]
      );
      message.success(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_SAVE_SUCCESS));
      setModalOpen(false);
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_SAVE_ERROR));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (accessory: IMachineryAccessory) => {
    try {
      const updated = await updateMachineryAccessory(accessory.id, { isActive: !accessory.isActive });
      setAccessories((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_STATUS_UPDATE_ERROR));
    }
  };

  const handleDeleteAccessory = async () => {
    if (!deletingAccessory) return;
    setDeleteBusy(true);
    try {
      await deleteMachineryAccessory(deletingAccessory.id);
      setAccessories((prev) => prev.filter((a) => a.id !== deletingAccessory.id));
      setDeletingAccessory(undefined);
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_STATUS_UPDATE_ERROR));
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <AppLayout>
      <div style={{ width: '100%', maxWidth: 1100 }}>
        <PageHeaderRow
          title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORIES_NAV_LABEL)}
          subtitle={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORIES_PAGE_SUBTITLE)}
          actions={
            !loading && accessories.length > 0 ? (
              <Button type="primary" shape="round" icon={<PlusOutlined />} onClick={openCreateModal}>
                {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORIES_CREATE_BUTTON)}
              </Button>
            ) : undefined
          }
        />

        {loading ? (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Spin size="large" />
          </div>
        ) : accessories.length === 0 ? (
          <EmptyState
            icon={<IconTool size={24} />}
            title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORIES_EMPTY_TITLE)}
            description={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORIES_EMPTY_DESCRIPTION)}
            action={
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
                {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORIES_CREATE_BUTTON)}
              </Button>
            }
          />
        ) : (
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            {accessories.map((accessory) => (
              <Col xs={24} sm={12} md={8} key={accessory.id}>
                <Card bordered style={{ borderRadius: 18, opacity: accessory.isActive ? 1 : 0.6 }} styles={{ body: { padding: 16 } }}>
                  <span
                    style={{
                      display: 'inline-block',
                      alignSelf: 'flex-start',
                      marginBottom: 4,
                      padding: '2px 10px',
                      borderRadius: 999,
                      fontSize: 12,
                      fontWeight: 600,
                      ...pillToneStyle(accessory.isActive ? 'success' : 'default', token),
                    }}
                  >
                    {translate(t, accessory.isActive ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_ACTIVE_STATUS : TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_INACTIVE_STATUS)}
                  </span>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    {accessory.photoUrls[0] && (
                      <img
                        src={accessory.photoUrls[0]}
                        alt={accessory.name}
                        style={{ width: 40, height: 40, borderRadius: 10, objectFit: 'cover', flexShrink: 0 }}
                      />
                    )}
                    <Title level={5} style={{ margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {accessory.name}
                    </Title>
                  </div>
                  {accessory.description && (
                    <Text type="secondary" style={{ fontSize: 13, display: 'block', marginTop: 4 }}>
                      {accessory.description}
                    </Text>
                  )}
                  <Text strong style={{ display: 'block', marginTop: 10, fontSize: 15 }}>
                    +{accessory.pricePerDay.toFixed(2)}€/día
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 2 }}>
                    {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_QUANTITY_LABEL)}: {accessory.quantity}
                  </Text>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                    {accessory.compatibleCategories.split(',').filter(Boolean).map((cat) => (
                      <Tag key={cat} style={{ borderRadius: 999, margin: 0 }}>{categoryLabel(cat)}</Tag>
                    ))}
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <Button
                        shape="round"
                        block
                        style={{ height: 40, border: 'none', ...pillToneStyle('default', token) }}
                        icon={<EditOutlined />}
                        onClick={() => openEditModal(accessory)}
                      >
                        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_EDIT_BUTTON)}
                      </Button>
                      <Button
                        shape="round"
                        block
                        style={{ height: 40, border: 'none', ...pillToneStyle(accessory.isActive ? 'error' : 'success', token) }}
                        icon={accessory.isActive ? <StopOutlined /> : <CheckCircleOutlined />}
                        onClick={() => toggleActive(accessory)}
                      >
                        {translate(t, accessory.isActive ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_DEACTIVATE_BUTTON : TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_REACTIVATE_BUTTON)}
                      </Button>
                    </div>
                    <Button
                      shape="round"
                      block
                      style={{ height: 40, border: 'none', ...pillToneStyle('error', token) }}
                      icon={<DeleteOutlined />}
                      onClick={() => setDeletingAccessory(accessory)}
                    >
                      {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_CARD_DELETE_BUTTON)}
                    </Button>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        )}

        <Modal
          open={modalOpen}
          title={translate(t, editingAccessory ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_MODAL_EDIT_TITLE : TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_MODAL_CREATE_TITLE)}
          onCancel={() => setModalOpen(false)}
          onOk={handleSave}
          confirmLoading={saving}
          okText={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_SAVE_BUTTON)}
          okButtonProps={{ shape: 'round' }}
          cancelButtonProps={{ shape: 'round' }}
          destroyOnClose
        >
          <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
            {/* Only when creating, never when editing: an existing accessory's
                fields are already answered, and talking over them is the one
                thing this must not do. */}
            {!editingAccessory && <AccessoryDraftAssistant disabled={saving} onApplied={handleAiApplied} />}

            <Form.Item name="name" rules={[{ required: true, message: translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_FIELD_REQUIRED) }]}>
              <FloatingLabelInput label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_NAME_LABEL)} disabled={saving} />
            </Form.Item>
            <Form.Item name="description">
              <EmojiTextArea placeholder={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_DESCRIPTION_LABEL)} rows={2} disabled={saving} maxLength={500} />
            </Form.Item>
            <Form.Item label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_PRICE_PER_DAY_LABEL)} name="pricePerDay" rules={[{ required: true, message: translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_FIELD_REQUIRED) }]}>
              <InputNumber min={0} step={1} addonAfter="€/día" inputMode="decimal" style={{ width: '100%' }} disabled={saving} />
            </Form.Item>
            <Form.Item label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_QUANTITY_LABEL)} name="quantity" rules={[{ required: true, message: translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_FIELD_REQUIRED) }]}>
              <InputNumber min={1} step={1} style={{ width: '100%' }} disabled={saving} />
            </Form.Item>
            <Form.Item
              label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_INDEPENDENTLY_RENTABLE_LABEL)}
              name="isIndependentlyRentable"
              valuePropName="checked"
            >
              <Switch disabled={saving} />
            </Form.Item>
            <Form.Item
              label={<>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_COMPATIBLE_CATEGORIES_LABEL)}{aiMark('compatibleCategories')}</>}
              name="compatibleCategories"
              rules={[{ required: true, message: translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_FIELD_REQUIRED) }]}
            >
              <TaxonomyPicker multiple disabled={saving} />
            </Form.Item>
            <Form.Item label={<>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_TYPE_LABEL)}{aiMark('accessoryTypeCode')}</>} name="accessoryTypeCode">
              <AccessoryTypePicker
                disabled={saving}
                title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_TYPE_PICKER_TITLE)}
                placeholder={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_TYPE_PICKER_PLACEHOLDER)}
              />
            </Form.Item>

            <SpecSectionTitle>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_MECHANICAL_SECTION_TITLE)}</SpecSectionTitle>
            <div style={{ display: 'flex', gap: 12 }}>
              <Form.Item label={<>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_WEIGHT_RANGE_MIN_LABEL)}{aiMark('weightRangeMinTons')}</>} name="weightRangeMinTons" style={{ flex: 1 }}>
                <InputNumber min={0} step={0.1} style={{ width: '100%' }} disabled={saving} />
              </Form.Item>
              <Form.Item label={<>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_WEIGHT_RANGE_MAX_LABEL)}{aiMark('weightRangeMaxTons')}</>} name="weightRangeMaxTons" style={{ flex: 1 }}>
                <InputNumber min={0} step={0.1} style={{ width: '100%' }} disabled={saving} />
              </Form.Item>
            </div>
            <Form.Item label={<>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_COUPLER_TYPE_LABEL)}{aiMark('couplerType')}</>} name="couplerType">
              <Select allowClear options={couplerTypeOptions} disabled={saving} />
            </Form.Item>

            <SpecSectionTitle>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_HYDRAULIC_SECTION_TITLE)}</SpecSectionTitle>
            <Form.Item name="requiresHydraulics" valuePropName="checked" style={{ marginBottom: requiresHydraulics ? 12 : 0 }}>
              <Space>
                <Switch disabled={saving} />
                <Text>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_REQUIRES_HYDRAULICS_LABEL)}</Text>
              </Space>
            </Form.Item>
            {requiresHydraulics && (
              <>
                <div style={{ display: 'flex', gap: 12 }}>
                  <Form.Item label={<>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_HYDRAULIC_FLOW_MIN_LABEL)}{aiMark('hydraulicFlowMinLMin')}</>} name="hydraulicFlowMinLMin" style={{ flex: 1 }}>
                    <InputNumber min={0} step={1} style={{ width: '100%' }} disabled={saving} />
                  </Form.Item>
                  <Form.Item label={<>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_HYDRAULIC_FLOW_MAX_LABEL)}{aiMark('hydraulicFlowMaxLMin')}</>} name="hydraulicFlowMaxLMin" style={{ flex: 1 }}>
                    <InputNumber min={0} step={1} style={{ width: '100%' }} disabled={saving} />
                  </Form.Item>
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                  <Form.Item label={<>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_HYDRAULIC_PRESSURE_MIN_LABEL)}{aiMark('hydraulicPressureMinBar')}</>} name="hydraulicPressureMinBar" style={{ flex: 1 }}>
                    <InputNumber min={0} step={1} style={{ width: '100%' }} disabled={saving} />
                  </Form.Item>
                  <Form.Item label={<>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_HYDRAULIC_PRESSURE_MAX_LABEL)}{aiMark('hydraulicPressureMaxBar')}</>} name="hydraulicPressureMaxBar" style={{ flex: 1 }}>
                    <InputNumber min={0} step={1} style={{ width: '100%' }} disabled={saving} />
                  </Form.Item>
                </div>
                <Form.Item label={<>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_HYDRAULIC_LINES_LABEL)}{aiMark('hydraulicLines')}</>} name="hydraulicLines">
                  <Select allowClear options={opts(HYDRAULIC_LINES_OPTIONS)} disabled={saving} />
                </Form.Item>
                <Form.Item label={<>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_HYDRAULIC_CONNECTOR_TYPE_LABEL)}{aiMark('hydraulicConnectorType')}</>} name="hydraulicConnectorType">
                  <Select allowClear options={opts(HYDRAULIC_CONNECTOR_TYPE_OPTIONS)} disabled={saving} />
                </Form.Item>
                <Form.Item name="requiresHighFlow" valuePropName="checked">
                  <Space>
                    <Switch disabled={saving} />
                    <Text>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_REQUIRES_HIGH_FLOW_LABEL)}</Text>
                  </Space>
                </Form.Item>
                <Form.Item name="requiresCaseDrain" valuePropName="checked">
                  <Space>
                    <Switch disabled={saving} />
                    <Text>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_REQUIRES_CASE_DRAIN_LABEL)}</Text>
                  </Space>
                </Form.Item>
              </>
            )}

            <SpecSectionTitle>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_DIMENSIONS_SECTION_TITLE)}</SpecSectionTitle>
            <Form.Item label={<>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_WORKING_WIDTH_LABEL)}{aiMark('workingWidthMm')}</>} name="workingWidthMm">
              <InputNumber min={0} step={1} style={{ width: '100%' }} disabled={saving} />
            </Form.Item>
            <Form.Item label={<>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_CAPACITY_LABEL)}{aiMark('capacityLiters')}</>} name="capacityLiters">
              <InputNumber min={0} step={1} style={{ width: '100%' }} disabled={saving} />
            </Form.Item>
            <Form.Item label={<>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_OWN_WEIGHT_LABEL)}{aiMark('ownWeightKg')}</>} name="ownWeightKg">
              <InputNumber min={0} step={1} style={{ width: '100%' }} disabled={saving} />
            </Form.Item>

            <Form.Item label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_PHOTOS_LABEL)}>
              <PhotoUpload
                domain="accessories"
                value={photoUrls}
                thumbnails={photoThumbnails}
                onChange={(newPhotos, newThumbnails) =>
                  form.setFieldsValue({ photoUrlsText: newPhotos, photoThumbnailsText: newThumbnails })
                }
                disabled={saving}
              />
              <Form.Item name="photoUrlsText" hidden><input /></Form.Item>
              <Form.Item name="photoThumbnailsText" hidden><input /></Form.Item>
            </Form.Item>
          </Form>
        </Modal>
      </div>

      <ConfirmBottomSheet
        open={!!deletingAccessory}
        title={translate(t, TRANSLATIONS.DELETE_ITEM_CONFIRM_TITLE)}
        description={deletingAccessory?.name}
        cancelText={translate(t, TRANSLATIONS.DELETE_ITEM_CONFIRM_KEEP)}
        confirmText={translate(t, TRANSLATIONS.DELETE_ITEM_CONFIRM_DELETE)}
        confirmLoading={deleteBusy}
        onConfirm={handleDeleteAccessory}
        onClose={() => setDeletingAccessory(undefined)}
      />
    </AppLayout>
  );
};

// Split into a wrapper + Content component so that a scoped theme can
// be added here without moving anything: theme.useToken() only sees a
// nested ConfigProvider's tokens from a component mounted UNDER it, not
// from the one rendering the provider. There is nothing scoped here
// today -- coral is the app-wide primary (see theme/appTheme).
const MyMachineryAccessories: React.FC = () => {
  const { isMobile } = useAppBreakpoint();
  return isMobile ? (
    <MyMachineryAccessoriesContent />
  ) : (
    <MyMachineryAccessoriesContent />
  );
};

export default MyMachineryAccessories;
